import { formatCurrency, formatDate, resolveMediaUrl } from "./utils";
import { openHtmlDocument } from "./docViewer";
import { mergeLines } from "./invoicePrint";

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const STATUS_LABEL = { pending: "Pendente", accepted: "Aceite", rejected: "Rejeitado", converted: "Convertido em pedido" };

export const buildQuoteHtml = (q, settings = {}) => {
  const logoRaw = settings.logo_url || settings.logo_file || "";
  const logoSrc = logoRaw ? resolveMediaUrl(logoRaw) : "";
  const name = settings.restaurant_name || settings.business_name || "Empresa";
  const addressLine = [settings.address, settings.postal_code && settings.postal_code !== "0000" ? settings.postal_code : null, settings.city, settings.country === "AO" || !settings.country ? "Angola" : settings.country]
    .filter(Boolean)
    .join(" · ");
  const hasBank = settings.bank_name || settings.bank_iban || settings.bank_bic || settings.bank_account_holder;
  const rows = mergeLines(q.items)
    .map(
      (it, i) => `<tr>
        <td class="c mono">${i + 1}</td>
        <td>${esc(it.description)}</td>
        <td class="c mono">${it.quantity}</td>
        <td class="r mono">${formatCurrency(it.unit_price)}</td>
        <td class="c mono">${it.tax_rate}%</td>
        <td class="r mono">${formatCurrency(it.line_total)}</td></tr>`
    )
    .join("");

  return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><title>Orçamento ${esc(q.number)}</title>
<style>
@page{size:A4;margin:14mm}
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;font-size:12px;line-height:1.3}
@media screen{body{max-width:190mm;margin:16px auto;padding:0 8px}}
.mono{font-family:"Courier New",monospace}.r{text-align:right}.c{text-align:center}
.hd{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111;padding-bottom:8px}
.co{display:flex;gap:14px;align-items:flex-start;max-width:60%}
.logo{width:64px;height:64px;object-fit:contain;border:1px solid #ddd;border-radius:8px;padding:4px;background:#fff}
.co h1{font-size:20px;margin:0 0 4px;font-weight:800}
.co p{margin:1px 0;font-size:11.5px;color:#333}
.doc{text-align:right}
.doc h2{margin:0;font-size:15px;letter-spacing:1px;font-weight:800}
.doc .num{font-size:20px;font-weight:800;margin:4px 0}
.doc p{margin:1px 0;font-size:11.5px;color:#333}
.badge{display:inline-block;margin-top:6px;padding:2px 8px;border:1px solid #111;border-radius:4px;font-size:10px;font-weight:700;letter-spacing:.5px}
.cli{margin-top:10px;display:grid;grid-template-columns:1fr 1fr;gap:16px}
.box{border:1px solid #bbb;border-radius:6px;padding:6px 10px}
.box h3{margin:0 0 6px;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#555}
.box p{margin:2px 0}
table{width:100%;border-collapse:collapse;margin-top:10px}
th{border-top:2px solid #111;border-bottom:2px solid #111;padding:5px 6px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;background:#f4f4f4}
td{padding:4px 6px;border-bottom:1px solid #ddd;vertical-align:top}
.tot{margin-top:8px;display:flex;justify-content:flex-end}
.tot table{width:300px;margin:0}
.tot td{border:none;padding:4px 6px}
.tot .g td{border-top:2px solid #111;font-weight:800;font-size:15px;padding-top:8px}
.grid2{margin-top:12px;display:grid;grid-template-columns:1fr 1fr;gap:16px}
.sig{margin-top:26px;display:grid;grid-template-columns:1fr 1fr;gap:60px}
.sig div{border-top:1px solid #111;padding-top:6px;text-align:center;font-size:11px;color:#444}
.ft{margin-top:14px;border-top:1px solid #bbb;padding-top:8px;text-align:center;font-size:10px;color:#555}
</style></head><body>
<div class="hd">
  <div class="co">
    ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="Logo" onerror="this.style.display='none'"/>` : ""}
    <div>
      <h1>${esc(name)}</h1>
      ${settings.business_name && settings.business_name !== name ? `<p>${esc(settings.business_name)}</p>` : ""}
      <p><b>NIF:</b> ${esc(settings.nif || "—")}</p>
      ${addressLine ? `<p>${esc(addressLine)}</p>` : ""}
      ${settings.phone ? `<p><b>Tel:</b> ${esc(settings.phone)}</p>` : ""}
      ${settings.email ? `<p><b>Email:</b> ${esc(settings.email)}</p>` : ""}
      ${settings.website ? `<p>${esc(settings.website)}</p>` : ""}
    </div>
  </div>
  <div class="doc">
    <h2>ORÇAMENTO / PROFORMA</h2>
    <p class="num mono">${esc(q.number)}</p>
    <p><b>Data:</b> ${formatDate(q.created_at)}</p>
    ${q.valid_until ? `<p><b>Válido até:</b> ${esc(q.valid_until)}</p>` : ""}
    <span class="badge">${esc(STATUS_LABEL[q.status] || q.status || "")}</span>
  </div>
</div>

<div class="cli">
  <div class="box">
    <h3>Cliente</h3>
    <p><b>${esc(q.customer_name)}</b></p>
    <p>NIF: ${esc(q.customer_nif || "Consumidor Final")}</p>
    ${q.customer_address ? `<p>${esc(q.customer_address)}</p>` : ""}
    ${q.customer_phone ? `<p>Tel: ${esc(q.customer_phone)}</p>` : ""}
  </div>
  <div class="box">
    <h3>Condições</h3>
    <p>Moeda: ${esc(settings.currency || "AOA")} (${esc(settings.currency_symbol || "Kz")}) · Preços com IVA incluído nos totais</p>
    <p>Validade: ${q.valid_until ? esc(q.valid_until) : "30 dias a partir da data de emissão"}</p>
    <p>Pagamento: ${hasBank ? "transferência bancária (dados abaixo), numerário ou Multicaixa" : "numerário, Multicaixa ou transferência"}</p>
  </div>
</div>

<table>
  <thead><tr><th class="c" style="width:32px">#</th><th>Descrição</th><th class="c" style="width:60px">Qtd</th><th class="r" style="width:120px">Preço Unit.</th><th class="c" style="width:60px">IVA</th><th class="r" style="width:130px">Total</th></tr></thead>
  <tbody>${rows}</tbody>
</table>

<div class="tot"><table>
  <tr><td>Subtotal (s/ IVA)</td><td class="r mono">${formatCurrency(q.subtotal)}</td></tr>
  <tr><td>IVA</td><td class="r mono">${formatCurrency(q.tax)}</td></tr>
  <tr class="g"><td>TOTAL</td><td class="r mono">${formatCurrency(q.total)}</td></tr>
</table></div>

<div class="grid2">
  ${hasBank ? `<div class="box">
    <h3>Dados Bancários para Pagamento</h3>
    ${settings.bank_name ? `<p><b>Banco:</b> ${esc(settings.bank_name)}</p>` : ""}
    ${settings.bank_account_holder ? `<p><b>Titular:</b> ${esc(settings.bank_account_holder)}</p>` : ""}
    ${settings.bank_iban ? `<p><b>IBAN:</b> <span class="mono">${esc(settings.bank_iban)}</span></p>` : ""}
    ${settings.bank_bic ? `<p><b>BIC/SWIFT:</b> <span class="mono">${esc(settings.bank_bic)}</span></p>` : ""}
    <p style="font-size:10.5px;color:#555;margin-top:4px">Indique o nº do orçamento <b>${esc(q.number)}</b> na referência da transferência.</p>
  </div>` : `<div class="box"><h3>Dados Bancários</h3><p style="color:#777">Configure os dados bancários em Configurações → Dados Bancários para aparecerem aqui.</p></div>`}
  <div class="box">
    <h3>Notas / Observações</h3>
    <p>${q.notes ? esc(q.notes).replace(/\n/g, "<br/>") : "Orçamento sujeito a confirmação de disponibilidade. Os preços podem ser revistos após a data de validade."}</p>
  </div>
</div>

<div class="sig">
  <div>${esc(name)}<br/>Assinatura e carimbo</div>
  <div>${esc(q.customer_name)}<br/>Aceitação do cliente (assinatura e data)</div>
</div>

<div class="ft">
  ${esc(name)}${settings.nif ? ` · NIF ${esc(settings.nif)}` : ""}${addressLine ? ` · ${esc(addressLine)}` : ""}${settings.phone ? ` · ${esc(settings.phone)}` : ""}${settings.email ? ` · ${esc(settings.email)}` : ""}<br/>
  Este documento é um orçamento/proforma e <b>não serve de fatura</b>. Processado por LUMORA.
</div>
<script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
</body></html>`;
};

// Versão térmica 80mm (impressoras de talão) — mesma informação, compacta
export const buildQuoteThermalHtml = (q, settings = {}) => {
  const name = settings.restaurant_name || settings.business_name || "Empresa";
  const rows = mergeLines(q.items)
    .map((it) => {
      const qty = Number(it.quantity) || 0;
      const iva = it.tax_rate != null ? ` <span class="tax">IVA ${it.tax_rate}%</span>` : "";
      if (qty === 1) return `<div class="flex item"><span>1 x ${esc(it.description)}${iva}</span><span class="amt">${formatCurrency(it.line_total)}</span></div>`;
      return `<div class="item"><div>${esc(it.description)}</div><div class="flex sub"><span>${qty} x ${formatCurrency(it.unit_price)}${iva}</span><span class="amt">${formatCurrency(it.line_total)}</span></div></div>`;
    })
    .join("");
  return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><title>Orçamento ${esc(q.number)}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}@page{size:80mm auto;margin:0}
body{width:72mm;margin:0 auto;padding:2mm 1mm 4mm;font-family:Arial,sans-serif;font-size:11px;line-height:1.25;color:#000;font-weight:700}
.c{text-align:center}.sub{font-size:10px}.flex{display:flex;justify-content:space-between;gap:4px}.amt{white-space:nowrap;font-weight:800}.tax{font-size:9px}
h1{font-size:13px;font-weight:900}.dashed{border-top:1px dashed #000;margin:3px 0}.item{margin:2px 0}.item .sub{margin-left:2mm}
.warn{border:1.5px solid #000;padding:2px;text-align:center;font-weight:900;margin:3px 0;font-size:10px}
.tline{display:flex;justify-content:space-between;font-size:11px;font-weight:800;margin:1px 0}
.total{display:flex;justify-content:space-between;font-size:15px;font-weight:900;border-top:2px solid #000;border-bottom:2px solid #000;padding:2px 0;margin:3px 0}
.ft{font-size:9px;margin-top:3px}
</style></head><body>
<div class="c"><h1>${esc(name)}</h1><p class="sub">NIF ${esc(settings.nif || "—")}${settings.phone ? ` · Tel ${esc(settings.phone)}` : ""}</p>
<p class="sub" style="font-size:12px;font-weight:900;margin-top:3px">ORÇAMENTO ${esc(q.number)}</p><p class="sub">${formatDate(q.created_at)}${q.valid_until ? ` · válido até ${esc(q.valid_until)}` : ""}</p></div>
<div class="warn">PROFORMA — NÃO SERVE DE FACTURA</div>
<div class="dashed"></div>
<p class="sub">Cliente: <b>${esc(q.customer_name)}</b> · NIF ${esc(q.customer_nif || "Consumidor Final")}</p>
<div class="dashed"></div>
${rows}
<div class="dashed"></div>
<div class="tline sub"><span>Subtotal s/ IVA</span><span>${formatCurrency(q.subtotal)}</span></div>
<div class="tline sub"><span>IVA</span><span>${formatCurrency(q.tax)}</span></div>
<div class="total"><span>TOTAL</span><span>${formatCurrency(q.total)}</span></div>
${settings.bank_iban ? `<p class="sub">${settings.bank_name ? `${esc(settings.bank_name)} · ` : ""}IBAN ${esc(settings.bank_iban)}</p>` : ""}
${q.notes ? `<p class="sub">${esc(q.notes)}</p>` : ""}
<div class="dashed"></div>
<p class="c ft">Orçamento sujeito a confirmação de disponibilidade · Processado por LUMORA</p>
<script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
</body></html>`;
};

export const printQuote = (q, settings = {}, format = null) => {
  const chosen = format || settings.print_format || "a4";
  const html = chosen === "thermal80" ? buildQuoteThermalHtml(q, settings) : buildQuoteHtml(q, settings);
  openHtmlDocument(html, { title: `Orçamento ${q.number || q.quote_number || ""}`.trim() });
};
